import { useState, useCallback } from 'react';

interface FormActionsParams {
  sectionsJsonText: string;
  questionsJsonText: string;
  currentFormDefinition: PolicyFormDefinition;
  hasOverride: boolean;
  onFormDefinitionChange: (newDefinition: PolicyFormDefinition | undefined) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  handleJsonError: (
    sectionsText: string,
    questionsText: string,
    setActiveTab: (tab: string) => void
  ) => void;
  setIsOpen: (open: boolean) => void;
  setSectionsJsonText: (text: string) => void;
  setQuestionsJsonText: (text: string) => void;
  setActiveTab: (tab: string) => void;
}

interface FormActionsResult {
  copied: boolean;
  handleApplyChanges: () => void;
  handleCopyFormDefinition: () => void;
  handleClearOverride: () => void;
}

export function useFormActions({
  sectionsJsonText,
  questionsJsonText,
  currentFormDefinition,
  hasOverride,
  onFormDefinitionChange,
  setError,
  clearError,
  handleJsonError,
  setIsOpen,
  setSectionsJsonText,
  setQuestionsJsonText,
  setActiveTab,
}: FormActionsParams): FormActionsResult {
  const [copied, setCopied] = useState(false);

  const handleApplyChanges = useCallback(() => {
    try {
      const parsedSections = JSON.parse(sectionsJsonText);
      const parsedQuestions = JSON.parse(questionsJsonText);

      // Validate sections structure
      if (!Array.isArray(parsedSections)) {
        throw new Error('Sections must be an array');
      }

      // Validate questions structure
      if (!parsedQuestions || typeof parsedQuestions !== 'object') {
        throw new Error('Questions must be an object');
      }

      // Combine into full form definition
      const fullFormDefinition: PolicyFormDefinition = {
        sections: parsedSections,
        questions: parsedQuestions,
        requiredParameters: currentFormDefinition.requiredParameters, // Preserve original
      };

      // Apply changes
      onFormDefinitionChange(fullFormDefinition);
      clearError();
      setIsOpen(false);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Invalid JSON';
      setError(errorMessage);
      handleJsonError(sectionsJsonText, questionsJsonText, setActiveTab);
    }
  }, [
    sectionsJsonText,
    questionsJsonText,
    currentFormDefinition.requiredParameters,
    onFormDefinitionChange,
    setError,
    clearError,
    handleJsonError,
    setIsOpen,
    setActiveTab,
  ]);

  const handleClearOverride = useCallback(() => {
    onFormDefinitionChange(undefined);
    setSectionsJsonText(JSON.stringify(currentFormDefinition.sections, null, 2));
    setQuestionsJsonText(JSON.stringify(currentFormDefinition.questions, null, 2));
    clearError();
    setIsOpen(false);
  }, [
    onFormDefinitionChange,
    currentFormDefinition,
    clearError,
    setIsOpen,
    setSectionsJsonText,
    setQuestionsJsonText,
  ]);

  const handleCopyFormDefinition = useCallback(() => {
    try {
      let fullFormDefinition: PolicyFormDefinition;

      if (hasOverride) {
        // Build from current editor content
        const parsedSections = JSON.parse(sectionsJsonText);
        const parsedQuestions = JSON.parse(questionsJsonText);

        fullFormDefinition = {
          sections: parsedSections,
          questions: parsedQuestions,
          requiredParameters: currentFormDefinition.requiredParameters,
        };
      } else {
        // Use original form definition
        fullFormDefinition = currentFormDefinition;
      }

      navigator.clipboard.writeText(JSON.stringify(fullFormDefinition, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError('Failed to copy form definition - invalid JSON');
    }
  }, [
    sectionsJsonText,
    questionsJsonText,
    currentFormDefinition,
    hasOverride,
    setError,
  ]);

  return {
    copied,
    handleApplyChanges,
    handleCopyFormDefinition,
    handleClearOverride,
  };
}